import { useMutation } from '@tanstack/react-query'
import { authFetch } from '@/api/client'
import { apiUrl } from '@/lib/apiUrl'
import { ApiError, type ProblemDetail } from '@/lib/errors'
import type { Job } from '@/api/endpoints/jobs'

export interface StartYoutubeDownloadInput {
  url: string
}

export interface StartYoutubeDownloadResponse {
  job_id: string
}

/**
 * Shape of the snapshot the backend stashes in ``job.result.snapshot``
 * when ``mode="download"`` succeeds. Only the fields the UI actually
 * reads are typed — ``stats`` etc. exist but we ignore them here.
 */
export interface YoutubeDownloadSnapshot {
  /** `'done'` é o estado terminal de sucesso; o backend também emite outros
   *  valores. Era declarado `'done' | string`, união que o TypeScript colapsa
   *  em `string` — a literal não restringia nada e não aparecia no
   *  autocomplete, só sugeria uma precisão que o tipo não tinha. */
  status: string
  download_url?: string
  suggested_filename?: string
  /** Server-side filesystem path — needed only when downstream tools
   * (clip creator, silence remover) want to reuse the freshly-fetched
   * video without re-uploading it. Ignored by the plain downloader UI. */
  temp_path?: string
  stats?: {
    title?: string
    duration?: number
    file_size?: number
    thumbnail_url?: string
    /** Present only on legacy ``mode="import"`` runs — the id of the
     * VideoProject that was created for the downloaded file. */
    video_id?: string
    [key: string]: unknown
  }
}

async function readProblem(res: Response, fallback: string): Promise<never> {
  let problem: ProblemDetail | null = null
  const contentType = res.headers.get('content-type') ?? ''
  if (
    contentType.includes('application/json') ||
    contentType.includes('application/problem+json')
  ) {
    try {
      const body = (await res.json()) as ProblemDetail
      if (typeof body.title === 'string') problem = body
    } catch {
      /* ignore */
    }
  }
  const message = problem?.detail ?? problem?.title ?? `${fallback} (HTTP ${String(res.status)})`
  throw new ApiError(message, res.status, problem, null)
}

/**
 * Kick off a "just download it" YouTube fetch. Returns the job id
 * immediately; poll it with {@link useJob} until ``status === 'succeeded'``,
 * then read {@link readDownloadSnapshot} to grab the download URL.
 */
export function useStartYoutubeDownload() {
  return useMutation({
    mutationFn: async (input: StartYoutubeDownloadInput): Promise<StartYoutubeDownloadResponse> => {
      const res = await authFetch('/api/v1/tools/youtube-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: input.url, mode: 'download' }),
        credentials: 'same-origin',
      })
      if (!res.ok) await readProblem(res, 'Falha ao iniciar download')
      const body = (await res.json()) as { data: StartYoutubeDownloadResponse }
      return body.data
    },
  })
}

/**
 * Pull the download snapshot off a finished job. Returns ``null`` if the
 * job is still running or if the backend didn't attach a snapshot
 * (e.g. legacy ``mode="import"`` runs don't set one).
 *
 * We accept ``Job | undefined`` so callers can pass ``useJob().data``
 * directly without an extra guard.
 */
export function readDownloadSnapshot(job: Job | undefined): YoutubeDownloadSnapshot | null {
  if (!job || job.status !== 'succeeded') return null
  const result = job.result as { snapshot?: YoutubeDownloadSnapshot } | null | undefined
  return result?.snapshot ?? null
}

/**
 * Build the absolute URL for the downloaded file. The backend serves it
 * from ``/output/...`` (see ``app.mount("/output", StaticFiles(...))`` in
 * ``apps/backend/src/web/app.py``), which lives on the API origin — so
 * we route through {@link apiUrl} to prefix ``VITE_API_BASE_URL`` in
 * production.
 */
export function resolveDownloadUrl(relativePath: string): string {
  return apiUrl(relativePath)
}
